import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, inr } from "@/lib/api";
import RatingModal from "@/components/RatingModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Loader2, KeyRound, RefreshCw, MapPin, Clock, ShoppingBag, Tag, Package,
  CheckCircle2, Star, Trash2, ShieldCheck, LogIn,
} from "lucide-react";

function hoursLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sold: "bg-crimson/10 text-crimson border-crimson/20",
  removed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function MyDeals() {
  const { user, ready } = useAuth();
  const { openAuth } = useOutletContext();
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingTxn, setRatingTxn] = useState(null);
  const [codes, setCodes] = useState({});
  const [busy, setBusy] = useState(null);
  const [armRemove, setArmRemove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tx, ml] = await Promise.all([api.get("/transactions"), api.get("/my-listings")]);
      setPurchases(tx.data.purchases || []);
      setSales(tx.data.sales || []);
      setMine(ml.data || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (user) load(); else setLoading(false);
  }, [ready, user, load]);

  const regenerate = async (txn) => {
    setBusy(txn.id);
    try {
      const { data } = await api.post(`/transactions/${txn.id}/regenerate-otp`);
      setPurchases((prev) => prev.map((p) => (p.id === txn.id ? { ...p, otp: data.otp, otp_expires_at: data.otp_expires_at } : p)));
      toast.success("A fresh code was generated.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const confirmHandoff = async (txn) => {
    const code = (codes[txn.id] || "").trim();
    if (code.length < 6) return toast.error("Enter the 6-digit code from your buyer.");
    setBusy(txn.id);
    try {
      const { data } = await api.post(`/transactions/${txn.id}/confirm-otp`, { code });
      toast.success("Handoff confirmed — the item is now sold.");
      await load();
      setRatingTxn(data.transaction);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const removeListing = async (l) => {
    setBusy(l.id);
    try {
      await api.post(`/listings/${l.id}/remove`);
      toast.success("Listing removed from the marketplace.");
      setArmRemove(null);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (ready && !user) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="deals-login-gate">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-brand/10 grid place-items-center mb-5"><Package className="text-brand" size={28} /></div>
        <h1 className="font-display font-bold text-2xl text-ink">Your deals live here</h1>
        <p className="mt-2 text-slate-500">Log in to see your purchases, pending sales and listings.</p>
        <Button onClick={openAuth} className="mt-6 bg-ink hover:bg-slate-800 text-white h-11 rounded-full px-6" data-testid="deals-login-btn">
          <LogIn size={17} className="mr-2" /> Log in / Sign up
        </Button>
      </div>
    );
  }

  const pendingBuys = purchases.filter((p) => p.status === "awaiting_handoff");
  const doneBuys = purchases.filter((p) => p.status === "completed");
  const pendingSales = sales.filter((s) => s.status === "awaiting_handoff");
  const doneSales = sales.filter((s) => s.status === "completed");

  return (
    <div className="mx-auto max-w-4xl px-4 lg:px-8 py-8 lg:py-10" data-testid="deals-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-ink text-3xl lg:text-4xl tracking-tight">My Deals</h1>
          <p className="text-slate-500 mt-1.5">Handoffs are confirmed with a one-time code. Money is exchanged directly, in person.</p>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="animate-spin" size={28} /></div>
      ) : (
        <div className="mt-8 space-y-10">
          {/* BUYING */}
          <section data-testid="section-buying">
            <div className="flex items-center gap-2 mb-4"><ShoppingBag size={18} className="text-brand" /><h2 className="font-display font-bold text-xl text-ink">Buying</h2></div>
            {pendingBuys.length === 0 && doneBuys.length === 0 ? (
              <EmptyLine text="You haven't bought anything yet." />
            ) : (
              <div className="space-y-3">
                {pendingBuys.map((t) => (
                  <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`buying-pending-${t.id}`}>
                    <DealHead t={t} tag="Awaiting handoff" tagClass="bg-amber-50 text-amber-700 border-amber-200" who={`Seller · ${t.counterparty?.name}`} />
                    <div className="mt-3 rounded-xl border-2 border-dashed border-brand/40 bg-brand/5 p-4 text-center">
                      <div className="flex items-center justify-center gap-1.5 label-mono text-brand text-xs mb-1.5"><KeyRound size={13} /> Your handoff code</div>
                      <div className="font-mono font-extrabold text-3xl tracking-[0.3em] text-ink pl-[0.3em]" data-testid={`buy-otp-code-${t.id}`}>{t.otp}</div>
                      <div className="mt-1.5 text-xs text-slate-500">{hoursLeft(t.otp_expires_at) || "Valid 24h"}</div>
                    </div>
                    <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5"><ShieldCheck size={13} className="text-emerald-500 mt-0.5 shrink-0" /> Read this code to the seller after you've exchanged the item and payment.</p>
                    <Button variant="outline" size="sm" onClick={() => regenerate(t)} disabled={busy === t.id} className="mt-3 border-slate-300" data-testid={`regen-otp-btn-${t.id}`}>
                      {busy === t.id ? <Loader2 className="animate-spin" size={14} /> : <><RefreshCw size={13} className="mr-1.5" /> Regenerate code</>}
                    </Button>
                  </div>
                ))}
                {doneBuys.map((t) => (
                  <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`buying-done-${t.id}`}>
                    <DealHead t={t} tag="Completed" tagClass="bg-emerald-50 text-emerald-700 border-emerald-200" who={`Seller · ${t.counterparty?.name}`} />
                    <RateRow t={t} label="Rate seller" onRate={() => setRatingTxn(t)} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* SELLING */}
          <section data-testid="section-selling">
            <div className="flex items-center gap-2 mb-4"><Tag size={18} className="text-crimson" /><h2 className="font-display font-bold text-xl text-ink">Selling</h2></div>
            {pendingSales.length === 0 && doneSales.length === 0 ? (
              <EmptyLine text="No sales yet. List an item to get started." />
            ) : (
              <div className="space-y-3">
                {pendingSales.map((t) => (
                  <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`sell-pending-${t.id}`}>
                    <DealHead t={t} tag="Buyer waiting" tagClass="bg-amber-50 text-amber-700 border-amber-200" who={`Buyer · ${t.counterparty?.name}`} />
                    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3.5">
                      <div className="text-sm font-medium text-ink flex items-center gap-1.5"><KeyRound size={14} className="text-brand" /> Enter the buyer's code at handoff</div>
                      <p className="text-xs text-slate-500 mt-1">Only enter it once you've received payment and handed over the item.</p>
                      <div className="mt-3 flex gap-2">
                        <Input
                          value={codes[t.id] || ""}
                          onChange={(e) => setCodes((c) => ({ ...c, [t.id]: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                          inputMode="numeric" maxLength={6} placeholder="6-digit code"
                          className="h-11 font-mono text-center tracking-[0.3em] text-lg"
                          data-testid={`confirm-otp-input-${t.id}`}
                        />
                        <Button
                          onClick={() => confirmHandoff(t)} disabled={busy === t.id || (codes[t.id] || "").length < 6}
                          className="h-11 bg-brand hover:bg-brand-700 text-white px-5 shrink-0" data-testid={`confirm-otp-btn-${t.id}`}
                        >
                          {busy === t.id ? <Loader2 className="animate-spin" size={16} /> : <><CheckCircle2 size={16} className="mr-1.5" /> Confirm</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {doneSales.map((t) => (
                  <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`sell-done-${t.id}`}>
                    <DealHead t={t} tag="Sold" tagClass="bg-crimson/10 text-crimson border-crimson/20" who={`Buyer · ${t.counterparty?.name}`} />
                    <RateRow t={t} label="Rate buyer" onRate={() => setRatingTxn(t)} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* MY LISTINGS */}
          <section data-testid="section-mylistings">
            <div className="flex items-center gap-2 mb-4"><Package size={18} className="text-ink" /><h2 className="font-display font-bold text-xl text-ink">My Listings</h2></div>
            {mine.length === 0 ? (
              <EmptyLine text="You have no listings yet." />
            ) : (
              <div className="space-y-3">
                {mine.map((l) => (
                  <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={`mylisting-${l.id}`}>
                    <img src={l.images?.[0]} alt={l.title} className="h-14 w-14 rounded-lg object-cover bg-slate-100" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-ink truncate">{l.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-display font-bold text-ink text-sm">{inr(l.price)}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-md border capitalize ${STATUS_BADGE[l.status] || STATUS_BADGE.active}`} data-testid={`listing-status-${l.id}`}>
                          {l.status === "removed" ? "Removed" : l.status === "sold" ? "Sold" : "Active"}
                        </span>
                      </div>
                    </div>
                    {l.status === "active" && (
                      armRemove === l.id ? (
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => setArmRemove(null)} className="h-9" data-testid={`remove-cancel-${l.id}`}>Keep</Button>
                          <Button size="sm" onClick={() => removeListing(l)} disabled={busy === l.id} className="h-9 bg-crimson hover:bg-crimson/90 text-white" data-testid={`remove-confirm-${l.id}`}>
                            {busy === l.id ? <Loader2 className="animate-spin" size={14} /> : "Remove"}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setArmRemove(l.id)} className="h-9 border-crimson/30 text-crimson hover:bg-crimson/5 shrink-0" data-testid={`remove-listing-btn-${l.id}`}>
                          <Trash2 size={14} className="mr-1.5" /> Remove
                        </Button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      <RatingModal open={!!ratingTxn} onOpenChange={(v) => !v && setRatingTxn(null)} transaction={ratingTxn} onRated={() => { setRatingTxn(null); load(); }} />
    </div>
  );
}

function DealHead({ t, tag, tagClass, who }) {
  return (
    <div className="flex items-center gap-3">
      <img src={t.listing?.image} alt={t.listing?.title} className="h-14 w-14 rounded-lg object-cover bg-slate-100" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-ink truncate">{t.listing?.title}</div>
        <div className="text-xs text-slate-500 truncate">{who}</div>
        {t.pickup_point && (
          <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate">
            <MapPin size={11} /> {t.pickup_point.name} · <Clock size={11} /> {t.pickup_point.hours}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display font-bold text-ink">{inr(t.price)}</div>
        <span className={`text-[11px] px-2 py-0.5 rounded-md border ${tagClass}`}>{tag}</span>
      </div>
    </div>
  );
}

function RateRow({ t, label, onRate }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
      {t.i_rated ? (
        <span className="text-sm text-emerald-600 font-medium inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> You rated this deal</span>
      ) : (
        <span className="text-sm text-slate-500">How was your counterpart?</span>
      )}
      {!t.i_rated && (
        <Button size="sm" onClick={onRate} className="bg-ink hover:bg-slate-800 text-white h-9" data-testid={`rate-btn-${t.id}`}>
          <Star size={14} className="mr-1.5" /> {label}
        </Button>
      )}
    </div>
  );
}

function EmptyLine({ text }) {
  return <p className="text-sm text-slate-400 rounded-xl border border-dashed border-slate-200 bg-white p-5 text-center">{text}</p>;
}
